import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import {
  Box,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import CustomSelect from "../../../components/customSelect";
import AddEditEcomSubSubCategoryDialog from "../../../components/Dialog/AddEditEcomSubSubCategoryDialog";
import CategoryBlockConfirmDialog from "../../../components/Dialog/CategoryBlockConfirmDialog";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getAllEcomCategory } from "../../../services/ecomCategoryApiFn";
import {
  getAllEcomSubCategory
} from "../../../services/ecomSubCategoryApiFn";
import { getAllEcomSubSubCategory, updateEcomSubSubCategoryStatus } from "../../../services/ecomSubSubCategoryApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = [
  "createdAt",
  "name",
  "order",
  "description",
  "status",
  "featured",
  "actions",
];

const tableRows = [
  "created_date",
  "ecoomerce_sub_sub_category",
  "serialNo",
  "ecom_category_description",
  "status",
  "featured",
  "actions",
];

function EcommerceSubSubCategory() {
  const dispatch = useDispatch();
  const openDialog = useRef(null);
  const openConfirmDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allCategory, setAllCategory] = useState([]);
  const { t } = useTranslation(["catalogue", "common"]);
  const [allSubCategory, setAllSubCategory] = useState([]);
  const [allSubSubCategory, setAllSubSubCategory] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  const debouncedSearchTerm = useDebounce(searchKeys.subCategoryName, 1000);

  useEffect(() => {
    fetchAllEcomSubSubCategory(debouncedSearchTerm, searchKeys.mainCategoryId, searchKeys.subCategoryId);
  }, [debouncedSearchTerm, searchKeys.mainCategoryId, searchKeys.subCategoryId]);

  useEffect(() => {
    fetchAllEcomCategory();
    handleFetchAllEcomSubCategory("All");
  }, []);

  const fetchAllEcomSubSubCategory = async (name, categoryId, subCategoryId) => {
    try {
      setIsLoading(true);
      const response = await getAllEcomSubSubCategory(
        name,
        categoryId,
        subCategoryId,
        currentLanguage
      );
      if (response.data.status === "success") {
        let data = response?.data.data.reverse();
        setAllSubSubCategory(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllEcomCategory = async () => {
    try {
      const response = await getAllEcomCategory();
      if (response.data.status === "success") {
        let data = response?.data.data.reverse();
        setAllCategory(data);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  const handleFetchAllEcomSubCategory = async (value) => {
    try {
      const response = await getAllEcomSubCategory("", value);
      if (response?.data?.status === "success") {
        setAllSubCategory(response?.data?.data);
      } else {
        handleOpenSnackBar(response?.data?.message, "error");
      }
    } catch (error) {
      handleOpenSnackBar(error?.message, "error");
    }
  };

  const handleEdit = (params) => {
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: fetchAllEcomSubSubCategory,
    });
  };

  const handleOpenConfirmDialog = (event, row, index) => {
    openConfirmDialog.current.openDialog({ event, row, index });
  };

  const handleChangeStatus = async (params) => {
    try {
      let payload = {
        status: params.event,
      };
      setIsLoading(true);
      const response = await updateEcomSubSubCategoryStatus(params.row._id, payload);

      if (response?.data?.status === "success") {
        openConfirmDialog.current.closeDialog();
        fetchAllEcomSubSubCategory(
          searchKeys.categoryName,
          searchKeys.mainCategoryId,
          searchKeys.subCategoryId
        );
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFeatured = async (event, row) => {
    try {
      let payload = {
        isFeatured: event,
      };
      setIsLoading(true);
      const response = await updateEcomSubSubCategoryStatus(row._id, payload);

      if (response?.data?.status === "success") {
        openConfirmDialog.current.closeDialog();
        fetchAllEcomSubSubCategory(
          searchKeys.categoryName,
          searchKeys.mainCategoryId,
          searchKeys.subCategoryId
        );
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };



  const prepareExportData = () => {
    return allSubSubCategory.map((item) => ({
      "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "N/A",
      "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "N/A",
      "Main Category Name In Romanian": item.category?.name?.ro || "N/A",
      "Main Category Name In English": item.category?.name?.en || "N/A",
      "Sub Category Name In Romanian": item.subCategory?.name?.ro || "N/A",
      "Sub Category Name In English": item.subCategory?.name?.en || "N/A",
      "Image": item.image || "N/A",
      "Small Icon": item.activeIcon || "N/A",
      "Slug In Romanian": item.mlSlug?.ro || "N/A",
      "Slug In English": item.mlSlug?.en || "N/A",
      "Name In Romanian": item.name?.ro || "N/A",
      "Name In English": item.name?.en || "N/A",
      "Description In Romanian": item.description?.ro || "N/A",
      "Description In English": item.description?.en || "N/A",
      "Sorting Number": item.serialNo || "N/A",
      "Status": item.status ? "Active" : "Inactive",
      "Featured": item.isFeatured ? "Yes" : "No",
      "Total Products At This Sub Sub Category": item.totalProducts || 0,
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          handleClickBtn={() => {
            openDialog.current.openDialog({
              type: "add",
              data: null,
              getData: fetchAllEcomSubSubCategory,
            });
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
        <CSVLink
          data={prepareExportData()}
          filename={"ecommerce_sub_sub_categories.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={t("export", { ns: "common" })}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            styleProps={{
              width: "auto",
              height: 40,
            }}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack
        p={2}
        gap={2}
        direction={{ md: "row" }}
        alignItems={"center"}
        bgcolor={"primary.light"}
      >
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.subCategoryName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      subCategoryName: "",
                    })
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.subCategoryName}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                subCategoryName: e.target.value,
              })
            );
          }}
        />
        <CustomSelect
          placeholder={t("category", { ns: "common" })}
          formStyleProps={{
            width: { xs: "100%", md: "25%" },
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<CategoryIcon sx={{ fontSize: "15px", color: "#000000" }} />}
          value={searchKeys.mainCategoryId}
          onChange={(e) => {
            const value = e.target.value;
            handleFetchAllEcomSubCategory(value)
            dispatch(
              setSearchKeys({
                ...searchKeys,
                mainCategoryId: value,
                subCategoryId: "All",
              })
            );
          }}
        >
          <MenuItem value={"All"}>{t("all", { ns: "common" })}</MenuItem>
          {allCategory.map((val, ind) => {
            return (
              <MenuItem
                key={ind}
                value={val._id}
                sx={{ textTransform: "capitalize" }}
              >
                {val.name[currentLanguage]}
              </MenuItem>
            );
          })}
        </CustomSelect>
        <CustomSelect
          placeholder={t("subCategory", { ns: "common" })}
          formStyleProps={{
            width: { xs: "100%", md: "25%" },
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<CategoryIcon sx={{ fontSize: "15px", color: "#000000" }} />}
          value={searchKeys.subCategoryId}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                subCategoryId: e.target.value,
              })
            );
          }}
          disabled={!searchKeys.mainCategoryId}
        >
          <MenuItem value={"All"}>{t("all", { ns: "common" })}</MenuItem>
          {allSubCategory.map((val, ind) => {
            return (
              <MenuItem
                key={ind}
                value={val._id}
                sx={{ textTransform: "capitalize" }}
              >
                {val.name[currentLanguage]}
              </MenuItem>
            );
          })}
        </CustomSelect>
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("ecommerceSubSubCategoryList")}
        subtitle={t("ecommerceSubSubCategoryDescription")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        <GlobalTable
          minWidth={1200}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allSubSubCategory || []}
          showActions={true}
          showEdit={true}
          handleEdit={handleEdit}
          handleChangeStatus={handleOpenConfirmDialog}
          handleChangeFeatured={handleChangeFeatured}
        />
        {allSubSubCategory?.length === 0 && isLoading === false && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <Typography variant="h5" fontWeight={600}>No data found</Typography>
          </Box>
        )}
      </PaperContainer>

      <AddEditEcomSubSubCategoryDialog
        ref={openDialog}
        allEcommerCategory={allCategory}
      />

      <CategoryBlockConfirmDialog
        ref={openConfirmDialog}
        handleConfirm={handleChangeStatus}
      />
    </>
  );
}

export default EcommerceSubSubCategory;
